import React, { createContext, useContext, useState, useEffect } from "react";
import { DataStore, SiteConfig } from "../types";
import * as initialData from "../data/mock";
import { APP_NAME, DISCORD_URL } from "../constants";
import {
  getAdminPassword,
  getEmployeePassword,
  fetchSiteDataFromSheets,
  syncSiteDataToSheets,
} from "../lib/sheets";

// Initial Configuration defaults
// ... (rest of the imports and defaultSiteConfig remains the same)
const defaultSiteConfig: SiteConfig = {
  appName: APP_NAME,
  discordUrl: DISCORD_URL,
  telegramUrl: "https://t.me/+z2UVUZe0lOs0ZjBh",
  facebookUrl: "https://www.facebook.com/gamesnolinux",
  instagramUrl: "https://www.instagram.com/gamesnolinux/",
  youtubeUrl: "https://www.youtube.com/@gnlofc",
  serverIp: "gamesnolinux.com.br",
  primaryColor: "#10b981",
  secondaryColor: "#fbbf24",
  heroImageUrl: "/logo_main.png",
  heroHeadline: "Nucleo Livre — Onde cada jogador é um Criador.",
  heroSubtext:
    "Princípio do Código Aberto: Nada pertence a um só. Junte-se a nós.",
  heroVideoUrl: "/hero_background.mp4",
  footerText:
    "Games no Linux — A história é escrita por todos sob o Selo da Liberdade.",
  welcomeMessage: "Bem-vindo ao Núcleo Livre, Aventureiro!",
  serverDescription:
    "Um servidor de Hytale regido pelo Código Aberto. Aqui, sua jornada molda o mundo e sua criatividade não tem limites.",
  featuredPlayerId: "p1",
  ctaTitle: "Games no Linux — A história é escrita por todos sob o Selo da Liberdade.",
  ctaDescription:
    "O mundo de Games no Linux está esperando por você. Junte-se a milhares de outros jogadores hoje.",
  trailerVideoUrl: "/onboarding_video.mp4",
  trailerPosterUrl: "/logo_main.png",
  sponsorUrl: "https://hadcloud.com.br/",
  sponsorName: "HadCloud",
  sponsorLogoUrl: "/logo-patrocinio.jpeg",
  meuSetupUrl: "https://meu-setup.vercel.app/",
  veracityDeclaration: "Ao enviar esta solicitação, declaro que todas as informações fornecidas são verdadeiras. Estou ciente de que é proibida a participação de menores de 18 anos e que, caso seja descoberto que os dados são falsos ou que sou menor de idade, minha conta será permanentemente banida do servidor.",
};

const initialState: DataStore = {
  siteConfig: defaultSiteConfig,
  onboardingConfig: initialData.onboardingConfig,
  serverStats: initialData.serverStats,
  regions: initialData.regions,
  cities: initialData.cities,
  clans: initialData.clans,
  players: initialData.players,
  updates: initialData.updates,
  mods: initialData.mods,
  blogPosts: initialData.blogPosts,
  gallery: initialData.gallery,
  skins: initialData.skins,
  rules: initialData.rules,
  routes: initialData.routes,
  paths: initialData.paths,
  records: initialData.records,
  supporters: [],
  events: [],
  sponsors: [],
  libraryConfig: initialData.libraryConfig,
  layout: initialData.siteLayout,
};

interface DataContextType {
  data: DataStore;
  updateData: (partialData: Partial<DataStore>) => void;
  publish: () => Promise<void>;
  isDirty: boolean;
  isAdmin: boolean;
  login: (password: string) => Promise<boolean>;
  logout: () => void;
  isEmployee: boolean;
  employeeLogin: (password: string) => Promise<boolean>;
  employeeLogout: () => void;
  showToast: (message: string, type?: "success" | "info" | "error") => void;
  isSupporterModalOpen: boolean;
  setIsSupporterModalOpen: (isOpen: boolean) => void;
  isRegistrationModalOpen: boolean;
  setIsRegistrationModalOpen: (isOpen: boolean) => void;
  clearLocalCache: () => void;
  isLoading: boolean;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

/**
 * Provedor de Dados Global (DataContext).
 * Gerencia o estado do site, autenticação e sincronização com o banco de dados.
 */
export const DataProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [data, setData] = useState<DataStore>(initialState);
  const [isDirty, setIsDirty] = useState(false);
  const [isAdmin, setIsAdmin] = useState(() => {
    return localStorage.getItem("gamesnolinux_admin_session") === "active";
  });
  const [isEmployee, setIsEmployee] = useState(() => {
    return localStorage.getItem("gamesnolinux_employee_session") === "active";
  });
  const [isSupporterModalOpen, setIsSupporterModalOpen] = useState(false);
  const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "info" | "error";
  } | null>(null);

  // Mostra uma notificação temporária (Toast) na tela
  const showToast = (
    message: string,
    type: "success" | "info" | "error" = "success",
  ) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Carrega os dados na inicialização do app (on mount)
  useEffect(() => {
    const mergeData = (remoteOrLocal: any): DataStore => ({
      ...initialState,
      ...remoteOrLocal,
      gallery:
        remoteOrLocal.gallery !== undefined
          ? remoteOrLocal.gallery
          : [...initialData.gallery],
      siteConfig: {
        ...initialState.siteConfig,
        ...(remoteOrLocal.siteConfig || {}),
        veracityDeclaration:
          remoteOrLocal.siteConfig?.veracityDeclaration ||
          initialState.siteConfig.veracityDeclaration,
      },
      libraryConfig: {
        ...initialState.libraryConfig,
        ...(remoteOrLocal.libraryConfig || {}),
      },
    });

    /**
     * Ordem de prioridade:
     * 1️⃣ Google Sheets (fonte da verdade — visível para TODOS os usuários)
     * 2️⃣ LocalStorage   (cache local — fallback se o Sheets estiver offline)
     * 3️⃣ Dados mock     (último recurso — primeira execução)
     */
    const loadData = async () => {
      let finalData: DataStore | null = null;

      // 1. Tentar carregar do Google Sheets
      try {
        const remoteData = await fetchSiteDataFromSheets();
        if (remoteData) {
          console.log("☁️ [SHEETS] Dados carregados do Google Sheets (visível para todos).");
          finalData = mergeData(remoteData);
          // Atualiza cache local com a versão mais recente
          localStorage.setItem("gamesnolinux_db", JSON.stringify(finalData));
        }
      } catch (e) {
        console.error("❌ [SHEETS] Erro ao carregar do Sheets:", e);
      }

      // 2. Fallback: LocalStorage
      if (!finalData) {
        try {
          const savedData = localStorage.getItem("gamesnolinux_db");
          if (savedData) {
            console.log("💾 [LOCAL] Sheets indisponível. Carregando do LocalStorage...");
            finalData = mergeData(JSON.parse(savedData));
          }
        } catch (err) {
          console.error("❌ [LOCAL] Erro ao ler cache:", err);
        }
      }

      // 3. Fallback final: dados mock
      if (!finalData) {
        console.log("✨ [INIT] Nenhum dado encontrado. Iniciando com dados originais (Mock).");
        finalData = initialState;
      }

      setData(finalData);
      setIsLoading(false);
    };

    loadData();

    const session = localStorage.getItem("gamesnolinux_admin_session");
    if (session === "active") setIsAdmin(true);
  }, []);

  // Apply CSS Variables dynamically based on config
  useEffect(() => {
    console.log("Applying CSS Variables with siteConfig:", data.siteConfig);
    const root = document.documentElement;
    if (data.siteConfig) {
      root.style.setProperty("--color-primary", data.siteConfig.primaryColor);
      root.style.setProperty(
        "--color-secondary",
        data.siteConfig.secondaryColor,
      );
    }
  }, [data.siteConfig]);

  // Atualiza o estado local e salva backup no LocalStorage
  const updateData = (partialData: Partial<DataStore>) => {
    setData((prev) => {
      const newData = { ...prev, ...partialData };
      // Salva rascunho imediato no LocalStorage para evitar perda de dados por reload acidental
      localStorage.setItem("gamesnolinux_db", JSON.stringify(newData));
      return newData;
    });
    setIsDirty(true); // Marca que existem mudanças pendentes de publicação
  };

  // Publica as alterações: salva no Google Sheets (para todos) + LocalStorage (cache)
  const publish = async () => {
    try {
      // 1. Salvar no Google Sheets — fonte da verdade para todos os usuários
      await syncSiteDataToSheets(data);

      // 2. Atualizar cache local
      localStorage.setItem("gamesnolinux_db", JSON.stringify(data));

      setIsDirty(false);
      showToast("✅ Alterações publicadas! Todos os usuários verão as mudanças.");
    } catch (error: any) {
      console.error("Publicação falhou:", error);
      // Salva ao menos localmente para não perder o trabalho
      localStorage.setItem("gamesnolinux_db", JSON.stringify(data));
      showToast(
        "Salvo localmente. Erro ao publicar no Sheets: " + (error?.message || ""),
        "error",
      );
    }
  };

  const login = async (password: string) => {
    const correctPassword = await getAdminPassword();
    // If correctPassword is null, it means DB fetch failed or table doesn't exist. Deny access.
    if (!correctPassword) return false;

    if (password.trim() === correctPassword.trim()) {
      setIsAdmin(true);
      localStorage.setItem("gamesnolinux_admin_session", "active");
      return true;
    }
    return false;
  };

  const logout = () => {
    setIsAdmin(false);
    localStorage.removeItem("gamesnolinux_admin_session");
  };

  const employeeLogin = async (password: string) => {
    const correctPassword = await getEmployeePassword();
    // If correctPassword is null, it means DB fetch failed or table doesn't exist. Deny access.
    if (!correctPassword) return false;

    if (password.trim() === correctPassword.trim()) {
      setIsEmployee(true);
      localStorage.setItem("gamesnolinux_employee_session", "active");
      return true;
    }
    return false;
  };

  const employeeLogout = () => {
    setIsEmployee(false);
    localStorage.removeItem("gamesnolinux_employee_session");
  };

  // Função para limpar cache local e forçar recarregamento
  const clearLocalCache = () => {
    localStorage.removeItem("gamesnolinux_db");
    localStorage.removeItem("gamesnolinux_admin_session");
    localStorage.removeItem("gamesnolinux_employee_session");
    showToast("Cache local limpo! Recarregando...", "info");
    setTimeout(() => window.location.reload(), 1500);
  };

  return (
    <DataContext.Provider
      value={{
        data,
        updateData,
        publish,
        isDirty,
        isAdmin,
        login,
        logout,
        isEmployee,
        employeeLogin,
        employeeLogout,
        showToast,
        isSupporterModalOpen,
        setIsSupporterModalOpen,
        isRegistrationModalOpen,
        setIsRegistrationModalOpen,
        clearLocalCache,
        isLoading,
      }}
    >
      {children}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] animate-slide-up">
          <div
            className={`
            px-6 py-3 rounded-full shadow-2xl backdrop-blur-xl border flex items-center gap-3
            ${
              toast.type === "success"
                ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-400"
                : toast.type === "error"
                  ? "bg-red-500/10 border-red-500/20 text-red-400"
                  : "bg-blue-500/10 border-blue-500/20 text-blue-400"
            }
          `}
          >
            <div
              className={`w-2 h-2 rounded-full animate-pulse ${toast.type === "success" ? "bg-emerald-400" : toast.type === "error" ? "bg-red-400" : "bg-blue-400"}`}
            ></div>
            <span className="text-sm font-bold tracking-wide">
              {toast.message}
            </span>
          </div>
        </div>
      )}
    </DataContext.Provider>
  );
};

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) throw new Error("useData must be used within DataProvider");
  return context;
};
